import { useEffect, useMemo, useRef, useState } from "react";
import type { JoinFrameState, JoinPageHosts } from "../../types/customJoinPage";
import {
  RS_MESSAGE,
  buildFrameDocument,
  randomNonce,
  type FrameDocumentInput,
} from "./frameDocument";

export interface CustomJoinFrameProps {
  html: string;
  css: string;
  hosts: JoinPageHosts;
  cssVars?: FrameDocumentInput["cssVars"];
  /** Join-flow state pushed into the frame so the CTA can reflect it. */
  state?: JoinFrameState;
  /** Fired when the visitor activates a `data-rs-action="join"` element. */
  onJoin?: () => void;
  /** Fired for any other `data-rs-action`. */
  onAction?: (action: string) => void;
  /** Called before opening a link; return false to block. Default: https only. */
  shouldOpenLink?: (url: URL) => boolean;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  title?: string;
}

const DEFAULT_MIN = 320;
const DEFAULT_MAX = 8000;

/**
 * The sandboxed iframe that renders a brand-authored join page. Used by the
 * public join route and, unchanged, by the editor preview — so what the brand
 * sees is exactly what ships.
 *
 * Nothing inside the frame can reach this document: `sandbox="allow-scripts"`
 * alone gives it an opaque origin, and the meta CSP inside the document (see
 * `frameDocument.ts`) blocks everything but our own bridge script. Messages
 * are trusted only when they come from this frame's own window.
 */
export function CustomJoinFrame({
  html,
  css,
  hosts,
  cssVars,
  state = "join",
  onJoin,
  onAction,
  shouldOpenLink,
  minHeight = DEFAULT_MIN,
  maxHeight = DEFAULT_MAX,
  className = "",
  title = "Brand page",
}: CustomJoinFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(minHeight);
  const [ready, setReady] = useState(false);
  const nonce = useMemo(() => randomNonce(), []);

  const srcDoc = useMemo(
    () => buildFrameDocument({ html, css, hosts, nonce, cssVars }),
    [html, css, hosts, nonce, cssVars]
  );

  // A new document means a new bridge; wait for its READY before pushing state.
  useEffect(() => {
    setReady(false);
    setHeight(minHeight);
  }, [srcDoc, minHeight]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const frame = iframeRef.current;
      // Opaque origin reports as "null", so identity is the window itself.
      if (!frame || event.source !== frame.contentWindow) return;
      const data = event.data as { type?: string } & Record<string, unknown>;
      if (!data || typeof data.type !== "string") return;

      switch (data.type) {
        case RS_MESSAGE.READY:
          setReady(true);
          break;
        case RS_MESSAGE.HEIGHT: {
          const px = Number(data.px);
          if (Number.isFinite(px)) {
            setHeight(Math.min(maxHeight, Math.max(minHeight, Math.ceil(px))));
          }
          break;
        }
        case RS_MESSAGE.ACTION: {
          const action = String(data.action ?? "");
          if (action === "join") onJoin?.();
          else if (action) onAction?.(action);
          break;
        }
        case RS_MESSAGE.NAVIGATE: {
          const href = String(data.href ?? "");
          let url: URL | null = null;
          try {
            url = new URL(href);
          } catch {
            return;
          }
          if (url.protocol !== "https:") return;
          if (shouldOpenLink && !shouldOpenLink(url)) return;
          window.open(url.toString(), "_blank", "noopener,noreferrer");
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onJoin, onAction, shouldOpenLink, minHeight, maxHeight]);

  // Push join state whenever it changes, and once the bridge is listening.
  useEffect(() => {
    if (!ready) return;
    // Target origin must be "*": the frame's origin is opaque. The payload is
    // a single state word, nothing sensitive.
    iframeRef.current?.contentWindow?.postMessage({ type: RS_MESSAGE.STATE, state }, "*");
  }, [ready, state]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      loading="eager"
      className={`block w-full border-0 ${className}`}
      style={{ height, contain: "content" }}
    />
  );
}
